import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';

import { APIBaseChronos } from '@/api/hosts';
import type { OrganisationCreateRequestDto, OrganisationCreateResponseDto } from '@/api/types/backend-dto-types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

/**
 * Global-admin only. Creates the org in PropelAuth (optionally allowing Enterprise SSO)
 * and mirrors it locally. Resolves with the created org so the caller can show the
 * org id and SSO setup link; no success toast here because the dialog renders the result.
 */
export const useCreateOrganisation = () => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  const createOrganisation = async (data: OrganisationCreateRequestDto): Promise<OrganisationCreateResponseDto> => {
    const fetchConfig = getFetchConfig({ method: FetchMethod.POST, data });

    const response = await fetch(`${APIBaseChronos}/api/org`, fetchConfig);
    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({}))) as { message?: string | string[] };
      const message = Array.isArray(errorData.message) ? errorData.message.join(', ') : errorData.message;
      throw new Error(message ?? 'Failed to create organisation');
    }
    return (await response.json()) as OrganisationCreateResponseDto;
  };

  return useMutation({
    mutationFn: createOrganisation,
    onSuccess: () => {
      // The list query is keyed by this plain string, not a QueryEntity.
      void queryClient.invalidateQueries('all-orgs');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};
